/**
 * Hook personnalisé pour gérer la génération d'articles
 * Contient la logique de génération, polling et publication
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const useArticleGeneration = ({ user, sites }) => {
  // Job asynchrone
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  
  // Timer pour afficher le temps écoulé
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastPollTime, setLastPollTime] = useState(null);
  
  // États pour les articles générés
  const [generatedArticles, setGeneratedArticles] = useState(null);
  const [totalCost, setTotalCost] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishedArticles, setPublishedArticles] = useState({});
  
  // États de génération
  const [generating, setGenerating] = useState(false);
  const [scrapeAlert, setScrapeAlert] = useState(null);
  
  // Flag pour éviter les toasts multiples
  const completionNotifiedRef = useRef(false);
  
  // Clé sessionStorage unique par utilisateur
  const storageKey = user?.id ? `autoedito_${user.id}_generated_articles` : 'autoedito_generated_articles';

  // Restaurer les articles depuis sessionStorage au montage
  useEffect(() => {
    if (!user?.id) return;
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setGeneratedArticles(parsed.articles);
        setTotalCost(parsed.totalCost);
        if (parsed.publishedArticles) setPublishedArticles(parsed.publishedArticles);
      } catch (e) { /* ignore */ }
    }
  }, [user?.id, storageKey]);

  // Persister les articles générés dans sessionStorage
  useEffect(() => {
    if (generatedArticles && user?.id) {
      sessionStorage.setItem(storageKey, JSON.stringify({
        articles: generatedArticles,
        totalCost,
        publishedArticles,
      }));
    }
  }, [generatedArticles, totalCost, publishedArticles, user?.id, storageKey]);

  // Polling du statut du job
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await axios.get(`${API}/api/jobs/${jobId}/status`);
      setJobStatus(response.data);
      
      // Charger les articles générés au fur et à mesure (progressif)
      if (response.data.sites_status) {
        const newArticlesMap = { ...generatedArticles };
        let hasNewArticles = false;
        
        for (const site of response.data.sites_status) {
          if (site.article_id && site.status === 'completed' && !newArticlesMap[site.site_id]) {
            try {
              const articleRes = await axios.get(`${API}/api/articles/${site.article_id}`);
              const data = { ...articleRes.data, site_name: site.site_name, is_extra: site.is_extra || false };
              newArticlesMap[site.site_id] = data;
              hasNewArticles = true;
            } catch (e) {
              console.error('Error loading article:', e);
            }
          }
        }
        
        if (hasNewArticles) {
          setGeneratedArticles(newArticlesMap);
        }
      }
      
      if (response.data.status === 'completed') {
        setGenerating(false);
        setScrapeAlert(null);
        
        if (!completionNotifiedRef.current) {
          completionNotifiedRef.current = true;
          const linksTotal = response.data.cluster_total_links_injected || 0;
          if (linksTotal > 0) {
            toast.success(`${response.data.completed_sites} article(s) généré(s) — ${linksTotal} liens cluster injectés`);
          } else {
            toast.success(`${response.data.completed_sites} article(s) généré(s) !`);
          }
        }
        setTotalCost(response.data.total_cost_eur);

        // Recharger les articles pour récupérer les liens cluster
        try {
          const refreshed = { ...generatedArticles };
          for (const site of (response.data.sites_status || [])) {
            if (site.article_id && site.status === 'completed') {
              try {
                const r = await axios.get(`${API}/api/articles/${site.article_id}`);
                refreshed[site.site_id] = { ...r.data, site_name: site.site_name, is_extra: site.is_extra || false };
              } catch (_) { /* ignore */ }
            }
          }
          setGeneratedArticles(refreshed);
        } catch (e) { /* non critique */ }

        return true;
      } else if (response.data.status === 'error') {
        setGenerating(false);
        setCurrentJobId(null);
        setScrapeAlert(null);
        toast.error('Erreur lors de la génération');
        return true;
      } else if (response.data.status === 'cancelled') {
        setGenerating(false);
        setCurrentJobId(null);
        setScrapeAlert(null);
        toast.info('Génération annulée');
        return true;
      } else if (response.data.status === 'scrape_failed' && !scrapeAlert) {
        setScrapeAlert({ jobId });
      }
      return false;
    } catch (error) {
      console.error('Error polling job:', error);
      return false;
    }
  }, [generatedArticles, scrapeAlert]);

  // Démarrer le polling
  useEffect(() => {
    if (!currentJobId) return;
    
    const interval = setInterval(async () => {
      const shouldStop = await pollJobStatus(currentJobId);
      setLastPollTime(Date.now());
      if (shouldStop) {
        clearInterval(interval);
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [currentJobId, pollJobStatus]);

  // Timer pour le temps écoulé
  useEffect(() => {
    if (!generating || !generationStartTime) {
      setElapsedTime(0);
      return;
    }
    
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - generationStartTime) / 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [generating, generationStartTime]);

  // Gérer la décision de scraping
  const handleScrapeDecision = async (decision) => {
    if (!scrapeAlert?.jobId) return;
    try {
      await axios.post(`${API}/api/jobs/${scrapeAlert.jobId}/scrape-decision`, { decision });
      setScrapeAlert(null);
      if (decision === 'cancel') {
        setGenerating(false);
        setCurrentJobId(null);
        toast.info('Génération annulée');
      }
    } catch (e) {
      console.error('Error sending scrape decision:', e);
    }
  };

  // Lancer la génération
  const startGeneration = async (params) => {
    setGenerating(true);
    setGeneratedArticles(null);
    setPublishedArticles({});
    setJobStatus(null);
    setGenerationStartTime(Date.now());
    setLastPollTime(null);
    completionNotifiedRef.current = false;
    sessionStorage.removeItem(storageKey);
    
    try {
      const response = await axios.post(`${API}/api/jobs/create`, params);
      
      toast.success('Génération lancée !');
      
      setCurrentJobId(response.data.job_id);
      setJobStatus({
        status: 'pending',
        total_sites: response.data.total_sites,
        completed_sites: 0,
        sites_status: response.data.sites.map(s => ({
          ...s,
          status: 'pending',
          progress: 0
        }))
      });
      
      setTimeout(() => {
        document.getElementById('generated-articles')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      
      return { success: true };
    } catch (error) {
      console.error('Generation error:', error);
      
      let errorMsg = 'Erreur lors de la génération';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Délai dépassé - la génération prend trop de temps. Réessayez.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      setGenerating(false);
      return { success: false, error: errorMsg };
    }
  };

  // Publier un article
  const handlePublish = async (siteId, articleData, status, sitePrompts = {}, forcePublish = false) => {
    console.log('[handlePublish] Called with:', { siteId, articleId: articleData?.id || articleData?.article_id, status, forcePublish });
    
    // Validation du siteId
    if (!siteId) {
      console.error('[handlePublish] siteId is missing!');
      toast.error('Site non sélectionné - impossible de publier');
      return;
    }
    
    // Vérifier que le site existe toujours dans la liste des sites disponibles
    if (sites && sites.length > 0) {
      const siteExists = sites.find(s => s.id === siteId);
      if (!siteExists && !siteId.startsWith('extra_')) {
        console.error('[handlePublish] Site not found in available sites:', siteId);
        toast.error('Site WordPress introuvable. Les données de session sont peut-être obsolètes. Veuillez relancer une génération.', { duration: 8000 });
        return;
      }
    }
    
    setPublishing(true);
    try {
      const articleId = articleData.id || articleData.article_id;
      
      if (!articleId) {
        console.error('[handlePublish] articleId is missing!');
        toast.error('ID article manquant');
        setPublishing(false);
        return;
      }
      
      // Sauvegarder les modifications avant publication
      try {
        console.log('[handlePublish] Saving article...', articleId);
        await axios.put(`${API}/api/articles/${articleId}`, articleData);
        console.log('[handlePublish] Article saved');
      } catch (saveErr) { 
        console.warn('[handlePublish] Save warning (non-blocking):', saveErr.message);
      }
      
      console.log('[handlePublish] Publishing to WordPress...', { siteId, articleId, status });
      const response = await axios.post(`${API}/api/wordpress-sites/${siteId}/publish`, {
        article_id: articleId,
        categories: [],
        tags: [],
        publish_status: status,
        custom_prompt: sitePrompts[siteId] || '',
        force_publish: forcePublish
      });
      console.log('[handlePublish] Publish response:', response.data);
      
      // Gestion de l&apos;avertissement SEO
      if (response.data?.warning) {
        const score = response.data.seo_score;
        setPublishing(false);
        
        toast.custom(
          (toastId) => (
            <div className="bg-slate-900 border border-amber-500/50 rounded-lg p-4 shadow-lg max-w-sm">
              <p className="font-semibold text-amber-400">Score SEO faible ({score}/100)</p>
              <p className="text-sm mt-1 text-slate-300">Voulez-vous publier quand même ?</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { toast.dismiss(toastId); handlePublish(siteId, articleData, status, sitePrompts, true); }}
                  className="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-sm font-semibold">
                  Forcer la publication
                </button>
                <button onClick={() => toast.dismiss(toastId)}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm">
                  Annuler
                </button>
              </div>
            </div>
          ),
          { duration: 30000 }
        );
        return;
      }
      
      const statusText = status === 'publish' ? 'publié' : 'enregistré en brouillon';
      const postUrl = response.data?.url || response.data?.link;
      
      setPublishedArticles(prev => ({
        ...prev,
        [siteId]: {
          status: status,
          url: postUrl,
          post_id: response.data?.post_id
        }
      }));
      
      if (postUrl) {
        toast.success(
          <div>
            <p>Article {statusText} sur {sites.find(s => s.id === siteId)?.name || 'WordPress'} !</p>
            <a href={postUrl} target="_blank" rel="noopener noreferrer" className="underline text-cyan-300 mt-1 block">
              Voir l&apos;article →
            </a>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.success(`Article ${statusText} !`);
      }
      
    } catch (error) {
      console.error('Publish error:', error);
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error('Erreur lors de la publication');
      }
    } finally {
      setPublishing(false);
    }
  };

  // Publication multi-sites avec délai
  const handlePublishAll = async (status, publishDelay = 0, sitePrompts = {}) => {
    const siteIds = Object.keys(generatedArticles || {});
    if (siteIds.length === 0) return;
    
    setPublishing(true);
    
    // Sauvegarder tous les articles avant publication
    for (const siteId of siteIds) {
      const articleData = generatedArticles[siteId];
      const articleId = articleData?.id || articleData?.article_id;
      if (articleId) {
        try {
          await axios.put(`${API}/api/articles/${articleId}`, articleData);
        } catch (saveErr) { /* non bloquant */ }
      }
    }
    
    for (let i = 0; i < siteIds.length; i++) {
      const siteId = siteIds[i];
      const articleData = generatedArticles[siteId];
      
      if (publishedArticles[siteId]) continue;
      
      const siteName = sites.find(s => s.id === siteId)?.name || siteId;
      
      if (i > 0 && publishDelay > 0) {
        const delayMs = publishDelay * 60 * 1000;
        toast.info(`Publication sur ${siteName} dans ${publishDelay} min...`, { duration: 5000 });
        
        const futureDate = new Date(Date.now() + delayMs * i);
        try {
          const response = await axios.post(`${API}/api/wordpress-sites/${siteId}/publish`, {
            article_id: articleData.id || articleData.article_id,
            publish_status: status,
            scheduled_date: futureDate.toISOString(),
            custom_prompt: sitePrompts[siteId] || '',
            force_publish: true
          });
          
          const postUrl = response.data?.url || response.data?.link;
          setPublishedArticles(prev => ({
            ...prev,
            [siteId]: { status: status === 'publish' ? 'future' : 'draft', url: postUrl, post_id: response.data?.post_id }
          }));
          toast.success(`${siteName} : planifié à ${futureDate.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}`);
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(detail || `Erreur pour ${siteName}`, { duration: 8000 });
        }
      } else {
        try {
          const response = await axios.post(`${API}/api/wordpress-sites/${siteId}/publish`, {
            article_id: articleData.id || articleData.article_id,
            publish_status: status,
            custom_prompt: sitePrompts[siteId] || '',
            force_publish: true
          });
          
          const postUrl = response.data?.url || response.data?.link;
          setPublishedArticles(prev => ({
            ...prev,
            [siteId]: { status: status, url: postUrl, post_id: response.data?.post_id }
          }));
          
          const statusText = status === 'publish' ? 'publié' : 'brouillon';
          toast.success(`${siteName} : ${statusText}`, { duration: 3000 });
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(detail || `Erreur pour ${siteName}`, { duration: 8000 });
        }
      }
    }
    setPublishing(false);
  };

  // Réinitialiser l&apos;état
  const resetGeneration = () => {
    setGeneratedArticles(null);
    setTotalCost(null);
    setPublishedArticles({});
    setCurrentJobId(null);
    setJobStatus(null);
    completionNotifiedRef.current = false;
    sessionStorage.removeItem(storageKey);
  };

  return {
    // États
    currentJobId,
    jobStatus,
    generationStartTime,
    elapsedTime,
    lastPollTime,
    generatedArticles,
    setGeneratedArticles,
    totalCost,
    publishing,
    publishedArticles,
    generating,
    scrapeAlert,
    storageKey,
    
    // Actions
    startGeneration,
    handlePublish,
    handlePublishAll,
    handleScrapeDecision,
    resetGeneration,
  };
};

export default useArticleGeneration;
